/**
 * High-Quality Audio Resampler
 *
 * Implements polyphase filter-based resampling for converting between
 * browser sample rates (typically 48kHz) and target rate (16kHz).
 *
 * Uses a windowed sinc FIR filter to eliminate aliasing artifacts
 * that occur with simple linear interpolation.
 */
export class AudioResampler {
  /**
   * Create a resampler
   * @param {number} inputRate - Input sample rate (e.g., 48000)
   * @param {number} outputRate - Output sample rate (e.g., 16000)
   */
  constructor(inputRate, outputRate) {
    this.inputRate = inputRate;
    this.outputRate = outputRate;
    this.ratio = inputRate / outputRate;

    // For common browser rates to 16kHz:
    // 48000 / 16000 = 3.0 (exact 3:1 decimation)
    // 44100 / 16000 = 2.75625 (requires interpolation)

    // Polyphase filter parameters
    this.filterLength = 32; // FIR filter taps
    this.halfFilterLength = this.filterLength / 2;

    // Design low-pass filter at Nyquist of output rate (8kHz for 16kHz output)
    this.cutoffFreq = outputRate / 2; // 8000 Hz
    this.filterCoeffs = this.designLowPassFilter();

    // State buffer for maintaining continuity between calls
    this.stateBuffer = new Float32Array(this.filterLength);
  }

  /**
   * Design windowed sinc low-pass FIR filter
   * Uses Hamming window to reduce ripple in passband
   * @returns {Float32Array} Filter coefficients
   */
  designLowPassFilter() {
    const coeffs = new Float32Array(this.filterLength);
    const fc = this.cutoffFreq / this.inputRate; // Normalized cutoff frequency
    const center = (this.filterLength - 1) / 2;

    let sum = 0;
    for (let i = 0; i < this.filterLength; i++) {
      const x = i - center;

      // Sinc function: sin(2πfc·x) / (π·x)
      let h;
      if (x === 0) {
        h = 2 * fc; // Limit as x→0
      } else {
        h = Math.sin(2 * Math.PI * fc * x) / (Math.PI * x);
      }

      // Hamming window: 0.54 - 0.46·cos(2πn/(N-1))
      const w = 0.54 - 0.46 * Math.cos(2 * Math.PI * i / (this.filterLength - 1));

      coeffs[i] = h * w;
      sum += coeffs[i];
    }

    // Normalize to unity gain at DC
    for (let i = 0; i < this.filterLength; i++) {
      coeffs[i] /= sum;
    }

    return coeffs;
  }

  /**
   * Resample audio from input rate to output rate
   * @param {Float32Array} inputData - Input audio samples
   * @returns {Float32Array} Resampled audio
   */
  resample(inputData) {
    if (this.ratio === 1.0) {
      // No resampling needed
      return inputData;
    }

    // Special case: exact integer decimation (e.g., 48kHz → 16kHz = 3:1)
    if (Math.abs(this.ratio - Math.round(this.ratio)) < 0.001) {
      return this.resampleDecimation(inputData);
    }

    // General case: arbitrary ratio (uses polyphase interpolation)
    return this.resamplePolyphase(inputData);
  }

  /**
   * Efficient decimation for integer ratios (e.g., 3:1)
   * @param {Float32Array} inputData
   * @returns {Float32Array}
   */
  resampleDecimation(inputData) {
    const decimationFactor = Math.round(this.ratio);
    const outputLength = Math.floor(inputData.length / decimationFactor);
    const output = new Float32Array(outputLength);

    // Combine state buffer with new input
    const combined = new Float32Array(this.stateBuffer.length + inputData.length);
    combined.set(this.stateBuffer);
    combined.set(inputData, this.stateBuffer.length);

    // Apply anti-aliasing filter and decimate
    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i * decimationFactor;
      let sum = 0;

      // Convolve with FIR filter
      for (let j = 0; j < this.filterLength; j++) {
        const idx = srcIndex + j;
        if (idx < combined.length) {
          sum += combined[idx] * this.filterCoeffs[j];
        }
      }

      output[i] = sum;
    }

    // Save last samples for next call
    const stateStart = combined.length - this.filterLength;
    if (stateStart >= 0) {
      this.stateBuffer.set(combined.subarray(stateStart));
    }

    return output;
  }

  /**
   * Polyphase resampling for arbitrary ratios
   * @param {Float32Array} inputData
   * @returns {Float32Array}
   */
  resamplePolyphase(inputData) {
    const outputLength = Math.floor(inputData.length / this.ratio);
    const output = new Float32Array(outputLength);

    // Combine state buffer with new input
    const combined = new Float32Array(this.stateBuffer.length + inputData.length);
    combined.set(this.stateBuffer);
    combined.set(inputData, this.stateBuffer.length);

    for (let i = 0; i < outputLength; i++) {
      const srcPos = i * this.ratio;
      const srcIndex = Math.floor(srcPos);
      const frac = srcPos - srcIndex;

      let sum = 0;

      // Apply polyphase filter with fractional delay compensation
      for (let j = 0; j < this.filterLength; j++) {
        const idx = srcIndex + j - this.halfFilterLength;
        if (idx >= 0 && idx < combined.length - 1) {
          // Linear interpolation for fractional delay
          const sample = combined[idx] * (1 - frac) + combined[idx + 1] * frac;
          sum += sample * this.filterCoeffs[j];
        } else if (idx >= 0 && idx < combined.length) {
          sum += combined[idx] * this.filterCoeffs[j];
        }
      }

      output[i] = sum;
    }

    // Save state for next call
    const stateStart = combined.length - this.filterLength;
    if (stateStart >= 0) {
      this.stateBuffer.set(combined.subarray(stateStart));
    }

    return output;
  }

  /**
   * Reset internal state (call when audio stream discontinuity occurs)
   */
  reset() {
    this.stateBuffer.fill(0);
  }

  /**
   * Get frequency response at given frequency (for testing)
   * @param {number} freq - Frequency in Hz
   * @returns {number} Magnitude response
   */
  getFrequencyResponse(freq) {
    const omega = 2 * Math.PI * freq / this.inputRate;
    let real = 0;
    let imag = 0;

    for (let i = 0; i < this.filterLength; i++) {
      real += this.filterCoeffs[i] * Math.cos(omega * i);
      imag += this.filterCoeffs[i] * Math.sin(omega * i);
    }

    return Math.sqrt(real * real + imag * imag);
  }
}
